import React, { useState, useEffect } from 'react';
import axios from 'axios';
import EventForm from './components/EventForm';
import ParticipantForm from './components/ParticipantForm';
import AdminPanel from './components/AdminPanel';
import EventList from './components/EventList';
import GameView from './components/GameView';
import Login from './components/Login';
import ContactsList from './components/ContactsList';
import CitiesList from './components/CitiesList';
import ParticipantImport from './components/ParticipantImport';
import AdminVoteModal from './components/AdminVoteModal';
import TicketsList from './components/TicketsList';
import BillingPage from './components/BillingPage';

function App() {
  const [adminToken, setAdminToken] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingParticipant, setEditingParticipant] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [votingParticipant, setVotingParticipant] = useState(null);
  const [view, setView] = useState('events'); // 'events', 'contacts', or 'cities'

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('TOKEN');
    const storedToken = localStorage.getItem('adminToken');
    const eventIdFromUrl = params.get('eventId');
    const viewFromUrl = params.get('view');

    if (viewFromUrl) {
      setView(viewFromUrl);
    }

    const activeToken = urlToken || storedToken;

    if (activeToken) {
      axios.get('/speeddate/api/cities', { headers: { 'x-admin-token': activeToken } })
        .then(() => {
          setAdminToken(activeToken);
          localStorage.setItem('adminToken', activeToken);
          axios.defaults.headers.common['x-admin-token'] = activeToken;

          if (urlToken) {
            params.delete('TOKEN');
            const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
            window.history.replaceState({}, '', newUrl);
          }

          fetchEvents(true, eventIdFromUrl);
        })
        .catch(() => {
          localStorage.removeItem('adminToken');
          setAdminToken(null);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const handleAdminLogin = async (token) => {
    try {
      setLoading(true);
      await axios.get('/speeddate/api/cities', { headers: { 'x-admin-token': token } });
      setAdminToken(token);
      localStorage.setItem('adminToken', token);
      axios.defaults.headers.common['x-admin-token'] = token;
      fetchEvents(false);
    } catch (err) {
      setLoading(false);
      window.Swal.fire({
        title: 'Accesso Fallito',
        text: 'Il token inserito non è valido.',
        icon: 'error'
      });
    }
  };

  const fetchEvents = async (refreshSelected = true, initialEventId = null) => {
    try {
      setLoading(true);
      const res = await axios.get('/speeddate/api/events');
      setEvents(res.data);

      const targetId = initialEventId || (selectedEvent ? selectedEvent.id : null);

      if (refreshSelected && targetId) {
        try {
          const detailRes = await axios.get(`/speeddate/api/events/${targetId}`);
          setSelectedEvent(detailRes.data);
        } catch (detailErr) {
          console.error("Error fetching event details", detailErr);
          const updated = res.data.find(e => String(e.id) === String(targetId));
          if (updated) setSelectedEvent(updated);
        }
      } else if (!targetId) {
        setSelectedEvent(null);
      }
    } catch (err) {
      console.error("Error fetching events", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEvent = async (event) => {
    const params = new URLSearchParams(window.location.search);
    params.set('eventId', event.id);
    window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);

    try {
      setLoading(true);
      const res = await axios.get(`/speeddate/api/events/${event.id}`);
      setSelectedEvent(res.data);
    } catch (err) {
      console.error("Error fetching details on select", err);
      setSelectedEvent(event);
    } finally {
      setLoading(false);
      window.scrollTo(0, 0);
    }
  };

  const handleBackToEvents = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete('eventId');
    params.set('view', 'events');
    window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
    setSelectedEvent(null);
    setEditingEvent(null);
    setView('events');
    fetchEvents(false);
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    window.scrollTo(0, 0);
  };

  const handleUpdateStatus = async (newStatus, title, text, icon, color, showEmailCheckbox = false) => {
    if (!selectedEvent) return;

    let confirmOptions = {
      title,
      icon,
      showCancelButton: true,
      confirmButtonColor: color || '#6366f1',
      cancelButtonColor: '#aaa',
      confirmButtonText: 'Sì, procedi!',
      cancelButtonText: 'Annulla',
      customClass: { popup: 'swal2-glass' }
    };

    if (showEmailCheckbox) {
      confirmOptions.html = `
        <div class="mb-3">${text}</div>
        <div class="form-check d-flex justify-content-center">
          <input class="form-check-input me-2" type="checkbox" id="sendEmailCheckbox" checked>
          <label class="form-check-label" for="sendEmailCheckbox" style="cursor:pointer">
            Invia email ai partecipanti
          </label>
        </div>
      `;
    } else {
      confirmOptions.text = text;
    }

    window.Swal.fire(confirmOptions).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const sendEmail = showEmailCheckbox ? document.getElementById('sendEmailCheckbox').checked : false;

          await axios.put(`/speeddate/api/events/${selectedEvent.id}`, { ...selectedEvent, status: newStatus });

          if (sendEmail) {
            axios.post(`/speeddate/api/events/${selectedEvent.id}/notify-start`).catch(err => console.error("Error sending start notifications", err));
          }

          fetchEvents();
          window.Swal.fire({
            title: 'Aggiornato!',
            text: sendEmail ? 'Stato aggiornato e email in fase di invio...' : `Stato dell'evento: ${title}`,
            icon: 'success',
            timer: 1500,
            showConfirmButton: false,
            customClass: { popup: 'swal2-glass' }
          });
        } catch (err) {
          window.Swal.fire('Errore', "Impossibile aggiornare lo stato", 'error');
        }
      }
    });
  };

  const handleSendResultsEmail = async () => {
    if (!selectedEvent) return;
    window.Swal.fire({
      title: 'Inviare i risultati?',
      text: "Ogni partecipante riceverà un'email con i propri match e statistiche.",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#aaa',
      confirmButtonText: 'Sì, Invia Email!',
      cancelButtonText: 'Annulla',
      customClass: { popup: 'swal2-glass' }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          window.Swal.fire({
            title: 'Invio in corso...',
            text: 'Stiamo preparando e inviando le email ai partecipanti.',
            allowOutsideClick: false,
            didOpen: () => { window.Swal.showLoading(); }
          });
          const res = await axios.post(`/speeddate/api/events/${selectedEvent.id}/send-results`);
          window.Swal.fire({
            title: 'Risultati Inviati!',
            text: `Email inviate con successo a ${res.data.sentCount} partecipanti.`,
            icon: 'success',
            customClass: { popup: 'swal2-glass' }
          });
        } catch (err) {
          window.Swal.fire('Errore', "Si è verificato un problema durante l'invio delle email.", 'error');
        }
      }
    });
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;
    window.Swal.fire({
      title: 'Sei sicuro?',
      text: "Tutti i partecipanti associati rimarranno, ma l'evento verrà rimosso!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sì, elimina!',
      cancelButtonText: 'Annulla'
    }).then(async (result) => {
      if (result.isConfirmed) {
        await axios.delete(`/speeddate/api/events/${selectedEvent.id}`);
        handleBackToEvents();
        window.Swal.fire('Eliminato!', 'L\'evento è stato rimosso.', 'success');
      }
    });
  };

  const path = window.location.pathname;
  const isLoginPage = path === '/admin' || path === '/admin/' || path === '/speeddate/admin' || path === '/speeddate/admin/';
  const isGamePage = path === '/' || path === '/speeddate' || path === '/speeddate/' || path.endsWith('/login') || path.endsWith('/login/');

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setAdminToken(null);
    setSelectedEvent(null);
    window.location.href = path.startsWith('/speeddate') ? '/speeddate/admin' : '/admin';
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  if (isGamePage) {
    return <GameView />;
  }

  if (!adminToken && isLoginPage) {
    return <Login onLogin={handleAdminLogin} />;
  }

  if (!adminToken) {
    return (
      <div className="container mt-5">
        <div className="card shadow-sm border-0 bg-white p-5 text-center">
          <i className="fas fa-user-shield fa-4x text-danger mb-4"></i>
          <h2 className="mb-3">Accesso Riservato</h2>
          <p className="text-muted mb-4">Per accedere al pannello è necessario utilizzare il link di amministrazione o effettuare il login.</p>
          <div className="d-flex justify-content-center gap-2">
            <a href={isLoginPage ? "#" : (path.startsWith('/speeddate') ? '/speeddate/admin' : '/admin')} className="btn btn-outline-primary rounded-pill px-4">
              Vai al Login <i className="fas fa-sign-in-alt ms-2"></i>
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Se siamo loggati ma non siamo sulla rotta admin, e non siamo sulla rotta game (già gestita)
  // Reindirizziamo l'admin alla sua pagina se prova ad accedere a rotte strane
  if (!isLoginPage && !isGamePage) {
    window.location.href = path.startsWith('/speeddate') ? '/speeddate/admin' : '/admin';
    return null;
  }

  return (
    <div className="min-vh-100 bg-light">
      {/* Top Navbar */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-primary shadow-sm fixed-top">
        <div className="container">
          <a className="navbar-brand d-flex align-items-center" href="#!" onClick={(e) => { e.preventDefault(); handleBackToEvents(); }}>
            <i className="fas fa-bolt me-2"></i>
            <strong>SpeedDate <span className="fw-light text-white-50">PRO</span></strong>
          </a>
          <div className="ms-auto d-flex align-items-center gap-3">
            <div className="d-flex align-items-center bg-white bg-opacity-10 rounded-pill p-1 me-2">
              <button
                className={`btn btn-sm rounded-pill px-3 border-0 ${view === 'events' ? 'btn-light text-primary shadow-sm' : 'text-white'}`}
                onClick={() => {
                  const params = new URLSearchParams(window.location.search);
                  params.set('view', 'events');
                  params.delete('eventId');
                  window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
                  setView('events');
                  setSelectedEvent(null);
                }}
              >
                <i className="fas fa-calendar-alt me-1"></i> Eventi
              </button>
              <button
                className={`btn btn-sm rounded-pill px-3 border-0 ${view === 'contacts' ? 'btn-light text-primary shadow-sm' : 'text-white'}`}
                onClick={() => {
                  const params = new URLSearchParams(window.location.search);
                  params.set('view', 'contacts');
                  params.delete('eventId');
                  window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
                  setView('contacts');
                  setSelectedEvent(null);
                }}
              >
                <i className="fas fa-address-book me-1"></i> Contatti
              </button>
              <button
                className={`btn btn-sm rounded-pill px-3 border-0 ${view === 'cities' ? 'btn-light text-primary shadow-sm' : 'text-white'}`}
                onClick={() => {
                  const params = new URLSearchParams(window.location.search);
                  params.set('view', 'cities');
                  params.delete('eventId');
                  window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
                  setView('cities');
                  setSelectedEvent(null);
                }}
              >
                <i className="fas fa-city me-1"></i> Città
              </button>
              <button
                className={`btn btn-sm rounded-pill px-3 border-0 ${view === 'tickets' ? 'btn-light text-primary shadow-sm' : 'text-white'}`}
                onClick={() => {
                  const params = new URLSearchParams(window.location.search);
                  params.set('view', 'tickets');
                  params.delete('eventId');
                  window.history.pushState({}, '', `${window.location.pathname}?${params.toString()}`);
                  setView('tickets');
                  setSelectedEvent(null);
                }}
              >
                <i className="fas fa-ticket-alt me-1"></i> Tipi Biglietto
              </button>
            </div>
            <button className="btn btn-sm btn-outline-light rounded-pill px-3" onClick={handleLogout}>
              <i className="fas fa-sign-out-alt me-1"></i> Esci
            </button>
          </div>
        </div>
      </nav>

      <main className="container py-5 mt-5">
        {editingEvent && (
          /* EDIT MODAL OVERLAY */
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
                <div className="modal-header border-0 pt-4 px-4">
                  <h5 className="modal-title fw-bold">Modifica Evento</h5>
                  <button type="button" className="btn-close" onClick={() => setEditingEvent(null)}></button>
                </div>
                <div className="modal-body p-4">
                  <EventForm
                    initialData={editingEvent}
                    onCreated={() => { setEditingEvent(null); fetchEvents(); }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {view === 'contacts' && !selectedEvent && (
          <div className="wow fadeIn">
            <ContactsList adminToken={adminToken} />
          </div>
        )}

        {view === 'cities' && !selectedEvent && (
          <div className="wow fadeIn">
            <CitiesList />
          </div>
        )}

        {view === 'tickets' && !selectedEvent && (
          <div className="wow fadeIn">
            <TicketsList />
          </div>
        )}

        {view === 'billing' && !selectedEvent && (
          <div className="wow fadeIn">
            <BillingPage />
          </div>
        )}

        {view === 'events' && !selectedEvent && (
          /* EVENT LIST VIEW */
          <div className="row g-4 wow fadeIn">
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm h-100 glass">
                <div className="card-header bg-transparent border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                  <h4 className="mb-0 fw-bold"><i className="fas fa-calendar-alt text-primary me-2"></i>Eventi Attivi</h4>
                  <button className="btn btn-sm btn-outline-primary rounded-circle" onClick={() => fetchEvents()}><i className="fas fa-sync"></i></button>
                </div>
                <div className="card-body px-4">
                  <EventList
                    events={events}
                    onSelect={handleSelectEvent}
                    onDelete={fetchEvents}
                    onEdit={handleEditEvent}
                  />
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="card border-0 shadow-lg h-100 bg-primary text-white overflow-hidden glass">
                <div className="card-body p-4 position-relative z-index-1">
                  <i className="fas fa-plus-circle position-absolute" style={{ top: -30, right: -30, fontSize: '180px', opacity: 0.15 }}></i>
                  <h4 className="fw-bold mb-4"><i className="fas fa-magic me-2"></i>Nuovo Evento</h4>
                  <EventForm onCreated={fetchEvents} lightMode={true} />
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedEvent && (
          /* SPECIFIC EVENT MANAGEMENT VIEW */
          <div className="wow fadeIn">
            <div className="d-flex align-items-center mb-4 glass p-3 rounded-4">
              <button className="btn btn-white shadow-sm btn-floating me-3" onClick={(e) => { e.preventDefault(); handleBackToEvents(); }}>
                <i className="fas fa-arrow-left"></i>
              </button>
              <div className="flex-grow-1">
                <h2 className="mb-1 fw-bold text-dark">{selectedEvent.name}</h2>
                <p className="text-muted mb-0 small" onClick={() => handleEditEvent(selectedEvent)} style={{ cursor: 'pointer' }}>
                  <i className="fas fa-map-marker-alt me-2"></i>{selectedEvent.location} &bull;
                  <i className="fas fa-clock ms-2 me-2"></i>{new Date(selectedEvent.date).toLocaleDateString()}
                  <i className="fas fa-edit ms-2 text-primary"></i>
                </p>
              </div>

              {selectedEvent.status >= 4 && (
                <div className="d-flex align-items-center gap-2 me-4">
                  <span className="badge bg-light text-muted border py-2 px-3 rounded-pill d-none d-lg-inline-block">
                    <i className="fas fa-lock me-2 text-secondary"></i>Iscrizioni Chiuse
                  </span>
                  {selectedEvent.status === 4 && (
                    <button className="btn btn-dark shadow-sm rounded-pill px-4"
                      onClick={() => handleUpdateStatus(5, 'Terminare il gioco?', 'Lo stato dell\'evento passerà a \'Terminato\'.', 'info', '#1e293b')}>
                      <i className="fas fa-stop-circle me-2"></i>Termina Gioco
                    </button>
                  )}
                  {selectedEvent.status >= 5 && (
                    <button className="btn btn-success shadow-sm rounded-pill px-4" onClick={handleSendResultsEmail}>
                      <i className="fas fa-paper-plane me-2"></i>Invia Risultati Match
                    </button>
                  )}
                </div>
              )}
              <div className="ms-auto d-none d-md-block">
                <div className="stats-badge d-flex gap-3">
                  <div className="bg-white bg-opacity-50 p-2 px-3 rounded-4 shadow-sm text-center border border-white" style={{ minWidth: '90px' }}>
                    <small className="text-uppercase text-muted d-block fw-bold" style={{ fontSize: '9px' }}>Totali</small>
                    <span className="h4 fw-bold m-0 text-dark">{selectedEvent.participants?.length || 0}</span>
                  </div>
                  <div className="bg-white bg-opacity-50 p-2 px-3 rounded-4 shadow-sm text-center border border-white" style={{ minWidth: '90px' }}>
                    <small className="text-uppercase text-primary d-block fw-bold" style={{ fontSize: '9px' }}>Uomini</small>
                    <span className="h4 fw-bold m-0 text-primary">{(selectedEvent.participants || []).filter(p => p.sex === 'male').length}</span>
                  </div>
                  <div className="bg-white bg-opacity-50 p-2 px-3 rounded-4 shadow-sm text-center border border-white" style={{ minWidth: '90px' }}>
                    <small className="text-uppercase text-danger d-block fw-bold" style={{ fontSize: '9px' }}>Donne</small>
                    <span className="h4 fw-bold m-0 text-danger">{(selectedEvent.participants || []).filter(p => p.sex === 'female').length}</span>
                  </div>
                </div>
              </div>
            </div>

            {showImport && (
              <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
                    <div className="modal-header border-0 pt-4 px-4">
                      <h5 className="modal-title fw-bold">Importazione Partecipanti (CSV)</h5>
                      <button type="button" className="btn-close" onClick={() => setShowImport(false)}></button>
                    </div>
                    <div className="modal-body p-4 pt-2">
                      <ParticipantImport
                        eventId={selectedEvent.id}
                        onImported={() => { setShowImport(false); fetchEvents(); }}
                        onCancel={() => setShowImport(false)}
                      />
                      <div className="mt-3 text-muted small px-2">
                        <i className="fas fa-info-circle me-2"></i>
                        Il file deve essere un .csv con separatore punto-virgola (;) e contenere le colonne Nominativo, Email, Ticket ID e Età.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {votingParticipant && (
              <AdminVoteModal
                participant={votingParticipant}
                onRefresh={fetchEvents}
                onClose={() => setVotingParticipant(null)}
              />
            )}

            <div className="row g-4">
              {/* Sidebar content (Add Participant & Actions) - First on mobile, Right on desktop */}
              {selectedEvent.status < 4 && (
                <div className="col-lg-4 order-lg-2">
                  {selectedEvent.status < 4 ? (
                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-body p-4">
                        <h5 className="fw-bold mb-4 border-bottom pb-2">
                          {editingParticipant ? (
                            <><i className="fas fa-edit text-success me-2"></i>Modifica Iscritto</>
                          ) : (
                            <div className="d-flex justify-content-between align-items-center">
                              <span><i className="fas fa-user-plus text-primary me-2"></i>Aggiungi Iscritto</span>
                              {/* <button className="btn btn-outline-primary btn-sm rounded-pill px-3" onClick={() => setShowImport(true)}>
                                <i className="fas fa-file-import me-1"></i> Importa CSV
                              </button> */}
                            </div>
                          )}
                        </h5>
                        <ParticipantForm
                          eventId={selectedEvent.id}
                          initialData={editingParticipant}
                          onCreated={() => {
                            setEditingParticipant(null);
                            fetchEvents();
                          }}
                          onCancel={() => setEditingParticipant(null)}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="card border-0 shadow-sm mb-4 bg-light">
                      <div className="card-body p-4 text-center">
                        <i className="fas fa-lock fa-2x text-muted mb-3 opacity-50"></i>
                        <h5 className="fw-bold text-muted">Iscrizioni Chiuse</h5>
                        <p className="small text-muted mb-0">Il gioco è in corso, non è più possibile aggiungere partecipanti.</p>
                      </div>
                    </div>
                  )}

                  <div className="card border-0 shadow-sm border-start border-primary border-4">
                    <div className="card-body p-4">
                      <h5 className="fw-bold mb-3">Azioni Evento</h5>
                      <div className="d-grid gap-2">
                        {selectedEvent.status < 3 && (
                          <button className="btn btn-info btn-sm mb-1 shadow-sm text-white"
                            onClick={() => handleUpdateStatus(3, 'Evento Iniziato', 'Lo stato dell\'evento passerà a \'Iniziato\'.', 'info', '#0dcaf0', true)}>
                            <i className="fas fa-play me-2"></i>Inizia Evento
                          </button>
                        )}
                        {selectedEvent.status === 3 && (
                          <button className="btn btn-primary btn-sm mb-1 shadow-sm" onClick={() => {
                            const hasMan = (selectedEvent.participants || []).some(p => p.sex === 'male' && p.arrivalNumber);
                            const hasWoman = (selectedEvent.participants || []).some(p => p.sex === 'female' && p.arrivalNumber);
                            if (!hasMan || !hasWoman) {
                              window.Swal.fire({
                                title: 'Attenzione',
                                text: "Non è possibile iniziare il gioco senza almeno un uomo e una donna che abbiano effettuato il check-in.",
                                icon: 'warning',
                                customClass: { popup: 'swal2-glass' }
                              });
                              return;
                            }
                            handleUpdateStatus(4, 'Gioco Iniziato', 'Lo stato dell\'evento passerà a \'Gioco\' e le iscrizioni verranno bloccate.', 'play', '#6366f1');
                          }}>
                            <i className="fas fa-rocket me-2"></i>Inizia Gioco
                          </button>
                        )}
                        {selectedEvent.status === 4 && (
                          <div className="text-center py-2">
                            <i className="fas fa-gamepad fa-2x text-primary mb-2 opacity-75"></i>
                            <p className="small text-muted mb-0">Gioco in corso.<br />Usa il pulsante in alto per terminare.</p>
                          </div>
                        )}

                        {selectedEvent.status < 4 && (
                          <button className="btn btn-outline-danger btn-sm" onClick={handleDeleteEvent}>
                            <i className="fas fa-trash-alt me-2"></i>Elimina Evento
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Main content (Participant Management) - Second on mobile, Left on desktop */}
              <div className={selectedEvent.status < 4 ? "col-lg-8 order-lg-1" : "col-lg-12"}>
                <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-0 pt-4 px-4">
                  <h5 className="fw-bold mb-0 text-dark">Gestione Partecipanti</h5>
                </div>
                  <div className="card-body p-0">
                    <AdminPanel
                      eventId={selectedEvent.id}
                      eventStatus={selectedEvent.status}
                      participants={selectedEvent.participants || []}
                      onRefresh={fetchEvents}
                      onVoteAs={(p) => setVotingParticipant(p)}
                      onEditParticipant={(p) => {
                        setEditingParticipant(p);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="py-4 bg-white border-top mt-auto text-center mt-5">
        <p className="mb-0 text-muted small">SpeedDate PRO &copy; 2024 <a href="https://www.virtualbar.top" target="_blank" rel="noopener noreferrer" className="text-decoration-none text-muted">https://www.virtualbar.top</a></p>
      </footer>
    </div>
  );
}

export default App;
